// Types
import { Set } from '@globalTypes/store';
import { DatePickerState, RangeDate, Tables } from './datePickerStore.types';

/**
 * A function that sets the selected date or year for a specific table.
 * @param set A function to update the state.
 * @returns A function that sets the selected date/year for a table.
 */
const setDate =
  (set: Set<DatePickerState>) =>
  (date: RangeDate, table: keyof typeof Tables): void =>
    set((state: DatePickerState) => {
      state[table].selectedDate = date; // Update the selected date/year for the specified table
    });

/**
 * A function that sets the available years for a specific table.
 * @param set A function to update the state.
 * @returns A function that sets the available years for a table.
 */
const setAvailableYears =
  (set: Set<DatePickerState>) =>
  (availableYears: number[], table: keyof typeof Tables): void =>
    set((state: DatePickerState) => {
      state[table].availableYears = availableYears; // Update the available years for the specified table
    });

const setDateLimits =
  (set: Set<DatePickerState>) =>
  (dateLimits: [Date, Date], table: keyof typeof Tables): void =>
    set((state: DatePickerState) => {
      state[table].dateLimits = dateLimits;
    });

/**
 * A collection of actions related to the date picker state.
 * @param set A function to update the state.
 * @returns An object containing functions to perform date picker actions.
 */
export const datePickerActions = (set: Set<DatePickerState>) => ({
  setDate: setDate(set), // Function to set the selected date/year for a table
  setAvailableYears: setAvailableYears(set), // Function to set the available years for a table
  setDateLimits: setDateLimits(set), // Function to set the available years for a table
});
